import React, { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { format } from "date-fns"
import dayjs from "dayjs"
import { orgNowLocal } from "@/lib/tz"
import { CalendarIcon, Loader2, AlertTriangle, CheckCircle2, XCircle, Info } from "lucide-react"
import { Badge } from "@/components/ui/badge"

// Final Date Processing Dialog Component
export default function FinalDateDialog({ 
  open, 
  onOpenChange, 
  employee, 
  employeeName, 
  onSuccess 
}) {
  const [finalDate, setFinalDate] = useState(() => orgNowLocal().toDate())
  const [processImmediately, setProcessImmediately] = useState(false)
  const [resignationReason, setResignationReason] = useState('')
  const [previewData, setPreviewData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [api, setApi] = useState(null)

  useEffect(() => {
    import('@/lib/api').then((module) => {
      setApi(module.api)
    })
  }, [])

  const validateForm = () => {
    if (!finalDate) {
      setError("Final Date is required")
      return false
    }
    setError(null)
    return true
  }

  const loadPreview = async () => {
    if (!api || !validateForm() || !employee) return
    
    setLoading(true)
    setError(null)
    
    try {
      const dateStr = dayjs(finalDate).format('YYYY-MM-DD')
      const data = await api.hrFinalDatePreview(employee, dateStr, processImmediately)
      setPreviewData(data)
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load preview: " + err.message)
      setPreviewData(null)
    } finally {
      setLoading(false)
    }
  }

  const processFinalDate = async () => {
    if (!api || !employee) return
    
    setProcessing(true)
    setError(null)
    setSuccess(null)
    
    try {
      const dateStr = dayjs(finalDate).format('YYYY-MM-DD')
      
      const result = await api.hrFinalDateProcess({
        employee: employee,
        final_date: dateStr,
        process_immediately: processImmediately,
        resignation_reason: resignationReason || undefined
      })
      
      setSuccess({
        message: "Worker final date processed successfully",
        result: result
      })
      
      if (onSuccess) {
        onSuccess(result)
      }
      
      await loadPreview()
      
    } catch (err) {
      const detail = err.response?.data?.detail
      if (detail && typeof detail === "object") {
        const failed = (detail.shifts_failed || []).map((f) => `${f.name}: ${f.error}`).join("; ")
        setError(`${detail.message || "Could not process final date."}${failed ? " Failed: " + failed : ""}`)
      } else {
        setError(detail || "Could not process final date: " + err.message)
      }
    } finally {
      setProcessing(false)
    }
  }

  const resetDialog = () => {
    setPreviewData(null)
    setError(null)
    setSuccess(null)
    setResignationReason('')
    setProcessImmediately(false)
    setFinalDate(orgNowLocal().toDate())
  }

  useEffect(() => {
    if (!open) {
      resetDialog()
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetDialog()
      onOpenChange(isOpen)
    }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="final-date-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {employeeName ? `Mark Final Date for ${employeeName}` : 'Mark Worker Final Date'}
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-green-800 mb-2">Processing Complete</h4>
                <p className="text-sm text-green-700 mb-3">{success.message}</p>
                <div className="text-sm text-green-700 space-y-2">
                  <div><strong>Worker Status:</strong> {success.result.employee?.new_status}</div>
                  <div><strong>Leave Records Created:</strong> {success.result.processing_summary?.leave_records_created_count}</div>
                  <div><strong>Shifts Processed:</strong> {success.result.processing_summary?.shifts_processed_count}</div>
                  {success.result.employee?.effective_date && (
                    <div><strong>Effective Date:</strong> {success.result.employee.effective_date}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Final Date</Label>
              <p className="text-xs text-slate-500 mb-2">The worker's last working day (inclusive). The worker is taken off all upcoming shifts from this date; the shifts stay on the roster board as unassigned.</p>
              <div className="flex gap-3 items-end">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className={cn("w-[240px] justify-start text-left font-normal h-9", !finalDate && "text-muted-foreground")}
                      data-testid="final-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {finalDate ? format(finalDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar 
                      mode="single" 
                      selected={finalDate}
                      onSelect={setFinalDate}
                      initialFocus
                      disabled={processing}
                    />
                  </PopoverContent>
                </Popover>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="process-immediately" 
                    checked={processImmediately}
                    onChange={(e) => setProcessImmediately(e.target.checked)}
                    disabled={processing}
                    className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    data-testid="process-immediately-checkbox"
                  />
                  <Label htmlFor="process-immediately" className="text-sm font-medium text-slate-700">Process Immediately</Label>
                </div>
              </div>
              {processImmediately && finalDate > orgNowLocal().toDate() && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <Info className="h-3 w-3" />Worker will be marked as departed today, not on the future final date
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Reason (Optional)</Label>
              <Input 
                value={resignationReason}
                onChange={(e) => setResignationReason(e.target.value)}
                placeholder="Brief reason for departure (e.g., Resignation, Termination, Career Change)"
                className="h-9 text-sm"
                disabled={processing}
                data-testid="resignation-reason"
              />
            </div>

            {previewData && (
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-800 mb-3">Preview of Changes</h4>

                {previewData.validation_messages && previewData.validation_messages.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {previewData.validation_messages.map((msg, idx) => (
                      <div key={idx} className={`p-3 rounded border ${msg.type === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="flex items-start gap-2 text-sm">
                          {msg.type === 'warning' ? (
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                          ) : (
                            <Info className="h-4 w-4 text-slate-600 mt-0.5" />
                          )}
                          <span className={msg.type === 'warning' ? 'text-amber-700' : 'text-slate-700'}>
                            {msg.message}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mb-4">
                  <h5 className="text-sm font-medium text-slate-700 mb-2">Shifts to Unassign ({previewData.shift_count})</h5>
                  {previewData.shifts_to_remove && previewData.shifts_to_remove.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3">
                      {previewData.shifts_to_remove.map((shift, idx) => (
                        <div key={idx} className="p-2 border border-slate-100 rounded bg-white text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{shift.participant_name}</span>
                            <span className="text-slate-500">• {shift.shift_date} {shift.start_time} - {shift.end_time}</span>
                            <Badge variant="outline" className="ml-auto text-[9px]" data-testid={`shift-status-${shift.name}`}>
                              {shift.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 p-3 border border-slate-200 rounded-lg">No shifts found on or after the final date.</p>
                  )}
                </div>

                {previewData.leave_records_to_create && previewData.leave_records_to_create.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-slate-700 mb-2">Leave Records to Create ({previewData.leave_record_count})</h5>
                    <div className="space-y-2 border border-slate-200 rounded-lg p-3">
                      {previewData.leave_records_to_create.map((leave, idx) => (
                        <div key={idx} className="p-2 border border-slate-100 rounded bg-white text-xs">
                          <div className="font-medium">{leave.leave_type}</div>
                          <div className="text-slate-600">{leave.days_remaining} days • {leave.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-3">
                <XCircle className="h-5 w-5 text-rose-600 mt-0.5" />
                <div className="text-sm text-rose-700">{error}</div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-slate-100 pt-4">
          {success ? (
            <Button 
              onClick={() => onOpenChange(false)} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="final-date-done"
            >Done
            </Button>
          ) : previewData ? (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={loadPreview}
                disabled={loading || processing}
                data-testid="final-date-reload-preview"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reload Preview'}
              </Button>
              <Button 
                onClick={processFinalDate}
                disabled={loading || processing}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="final-date-process"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm & Process'}
              </Button>
            </div>
          ) : (
            <Button 
              onClick={loadPreview}
              disabled={loading || processing}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="final-date-load-preview"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load Preview'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
